import { z } from "zod";

import { BinaryPersonGender, PersonGender } from "@/packages/shared/src/models/person";
import { properCase } from "@/src/lib/string";
import { addressSchema } from "@/src/models/address";
import { simpleDateSchema, phoneNumberSchema, emailSchema, ssnSchema } from "@/src/models/common-schema";
import {
  GIVEN_NAME_PROMPT,
  FAMILY_NAME_PROMPT,
  MIDDLE_NAME_PROMPT,
  NAME_PREFIX_PROMPT,
  NAME_SUFFIX_PROMPT,
} from "@/src/models/constants/prompt";
import { DatabaseDefaults } from "@/src/models/database-defaults";
export { BinaryPersonGender, PersonGender };

function normalizeEnumCasing<T extends string>(value: string | null, values: readonly T[]): T | string | null {
  if (value === null) return null;

  const normalized = value.trim().toLowerCase();

  return values.find((candidate) => candidate.toLowerCase() === normalized) ?? value;
}

/** Canonicalize known legacy casing without hiding genuinely invalid stored values. */
export function normalizePersonGender(value: string | null): PersonGender | string | null {
  return normalizeEnumCasing(value, Object.values(PersonGender));
}

/** Canonicalize known legacy casing without widening the binary-gender domain. */
export function normalizeBinaryPersonGender(value: string | null): BinaryPersonGender | string | null {
  return normalizeEnumCasing(value, Object.values(BinaryPersonGender));
}

export enum PersonRace {
  AMERICAN_INDIAN_ALASKA_NATIVE = "American Indian or Alaska Native",
  ASIAN = "Asian",
  BLACK_AFRICAN_AMERICAN = "Black or African American",
  NATIVE_HAWAIIAN_PACIFIC_ISLANDER = "Native Hawaiian or Other Pacific Islander",
  WHITE = "White",
  TWO_OR_MORE = "Two or More Races",
  DECLINED = "Declined to Specify",
  NOT_PROVIDED = "Not provided",
}

export enum PersonEthnicity {
  HISPANIC = "Hispanic or Latino",
  NOT_HISPANIC = "Not Hispanic or Latino",
  NOT_PROVIDED = "Not provided",
}

export enum PersonLanguage {
  ENGLISH = "English",
  SPANISH = "Spanish",
  FRENCH = "French",
  GERMAN = "German",
  CANTONESE = "Cantonese",
  MANDARIN = "Mandarin",
  ARABIC = "Arabic",
  HINDI = "Hindi",
  JAPANESE = "Japanese",
  KOREAN = "Korean",
  POLISH = "Polish",
  PORTUGUESE = "Portuguese",
  RUSSIAN = "Russian",
  VIETNAMESE = "Vietnamese",
  OTHER = "Other",
  NOT_PROVIDED = "Not provided",
}

const NON_TRANSLATABLE_PERSON_LANGUAGES: ReadonlySet<PersonLanguage> = new Set([
  PersonLanguage.ENGLISH,
  PersonLanguage.OTHER,
  PersonLanguage.NOT_PROVIDED,
]);

export function isTranslatablePersonLanguage(language: PersonLanguage | null | undefined): language is PersonLanguage {
  return language !== null && language !== undefined && !NON_TRANSLATABLE_PERSON_LANGUAGES.has(language);
}

// Empty/unknown sentinel enum values
export const PERSON_DEMOGRAPHIC_PLACEHOLDER_VALUES: ReadonlySet<string> = new Set([
  PersonGender.UNKNOWN,
  PersonEthnicity.NOT_PROVIDED,
  PersonRace.NOT_PROVIDED,
  PersonRace.DECLINED,
  PersonLanguage.NOT_PROVIDED,
]);

export const personalContactSchema = z.object({
  homePhoneNumber: phoneNumberSchema.nullable(),
  workPhoneNumber: phoneNumberSchema.nullable(),
  faxPhoneNumber: phoneNumberSchema.nullable(),
  mobilePhoneNumber: phoneNumberSchema.nullable(),
  personalEmail: emailSchema.nullable(),
  workEmail: emailSchema.nullable(),
  homeAddress: addressSchema.nullable(),
  workAddress: addressSchema.nullable(),
  emergencyContactName: z.string().nullable(),
  emergencyContactPhoneNumber: phoneNumberSchema.nullable(),
  emergencyContactRelationshipToPatient: z.string().nullable(),
});

export const personDemographicsSchema = z.object({
  ethnicity: z.nativeEnum(PersonEthnicity).default(PersonEthnicity.NOT_PROVIDED).describe(`
    This is the ethnicity of the patient.
    If you cannot determine the ethnicity, return "Not provided".
  `),
  language: z
    .nativeEnum(PersonLanguage)
    .nullable()
    .describe(
      `This is the preferred language of the patient. This is used to determine the language of the patient's communication with the provider.
      If you cannot determine the language, return "Not provided".`
    ),
  race: z.nativeEnum(PersonRace).default(PersonRace.NOT_PROVIDED).describe(`
    This is the race of the patient.
    If you cannot determine the race, return "Not provided".
  `),
  gender: z.nativeEnum(PersonGender).default(PersonGender.UNKNOWN),
  inferredGender: z.nativeEnum(BinaryPersonGender).describe(
    `
    This is the inferred gender of the patient either Male or Female.
    Infer the gender of the patient based on the given name.
    Or any other information you can gather from the document.
    Pick Male or Female only.
  `
  ),
  dateOfBirth: simpleDateSchema.nullable(),
  ssn: ssnSchema.nullable(),
});

export const personSchema = z.object({
  givenName: z.string().nullable().describe(GIVEN_NAME_PROMPT),
  familyName: z.string().nullable().describe(FAMILY_NAME_PROMPT),
  middleName: z.string().nullable().describe(MIDDLE_NAME_PROMPT),
  preferredName: z.string().nullable(),
  prefix: z.array(z.string()).nullable().describe(NAME_PREFIX_PROMPT),
  suffix: z.array(z.string()).nullable().describe(NAME_SUFFIX_PROMPT),
});

export type Person = z.infer<typeof personSchema> & z.infer<typeof personDemographicsSchema> & DatabaseDefaults;

type StrippablePersonDemographics = Partial<{
  gender: PersonGender | null;
  ethnicity: PersonEthnicity | null;
  race: PersonRace | null;
  language: PersonLanguage | null;
}>;

/** Maps demographic placeholder enums to null so generic merges treat them as empty. */
export function stripPersonDemographicPlaceholders<T extends StrippablePersonDemographics>(person: T): T {
  const result = { ...person };

  for (const field of ["gender", "ethnicity", "race", "language"] as const) {
    const value = result[field];

    if (typeof value === "string" && PERSON_DEMOGRAPHIC_PLACEHOLDER_VALUES.has(value)) {
      result[field] = null;
    }
  }

  return result;
}

// `getPersonReverseName` and `getPersonName` only read the three name fields,
// so accept any shape that provides them. This lets callers pass lighter
// projections (e.g. `PatientMetadata`) without coercing to the full `Person`.
type NameFields = {
  givenName: string | null;
  middleName?: string | null;
  familyName: string | null;
};

export function getPersonReverseName(person: NameFields | null | undefined, separator: string = ","): string | null {
  if (!person?.familyName) return null;

  return `${person.familyName}${separator}${person.givenName ?? ""}`;
}

export function getPersonName(
  person: NameFields | null | undefined,
  includeMiddleName: boolean = true,
  reverseOrderName: boolean = false,
  givenNameInitialOnly: boolean = false
): string {
  if (!person) {
    return "N/A";
  }

  const { givenName, middleName, familyName } = person;

  const formattedGivenName = givenNameInitialOnly && givenName ? `${givenName.charAt(0)}.` : givenName;

  if (reverseOrderName) {
    return [properCase(familyName), properCase(formattedGivenName), includeMiddleName ? properCase(middleName) : null]
      .filter(Boolean)
      .join(" ");
  }

  return [properCase(formattedGivenName), includeMiddleName ? properCase(middleName) : null, properCase(familyName)]
    .filter(Boolean)
    .join(" ");
}

export function personNamesMatch(person1: Person | null | undefined, person2: Person | null | undefined): boolean {
  if (!person1 || !person2) {
    return false;
  }

  return (
    person1.givenName?.toLowerCase().trim() === person2.givenName?.toLowerCase().trim() &&
    person1.familyName?.toLowerCase().trim() === person2.familyName?.toLowerCase().trim()
  );
}

export const personShape: Person = {
  id: "",
  givenName: null,
  familyName: null,
  middleName: null,
  gender: PersonGender.UNKNOWN,
  inferredGender: BinaryPersonGender.FEMALE,
  dateOfBirth: null,
  preferredName: null,
  prefix: [],
  suffix: [],
  ethnicity: PersonEthnicity.NOT_PROVIDED,
  language: null,
  race: PersonRace.NOT_PROVIDED,
  createdAt: "",
  updatedAt: "",
  deletedAt: null,
  groupId: "",
  ssn: null,
};

export function getGender(person: Person | null | undefined): PersonGender {
  if (!person) {
    return PersonGender.UNKNOWN;
  }

  if (person.gender === PersonGender.MALE || person.gender === PersonGender.FEMALE) {
    return person.gender;
  }

  if (person.inferredGender === BinaryPersonGender.MALE) {
    return PersonGender.MALE;
  }

  if (person.inferredGender === BinaryPersonGender.FEMALE) {
    return PersonGender.FEMALE;
  }

  return PersonGender.UNKNOWN;
}
